/** Rotas canônicas — mesma janela (MainLayout ou popup shell, conforme basePath). */

export const TEAM_BASE = "/equipe";
export const POPUP_TEAM_BASE = "/popup-equipe";
export const POPUP_CALL_BASE = "/popup-call";
export const TASKS_SEGMENT = "/tarefas";
export const AGENDA_SEGMENT = "/agenda";
export const SERVICE_SEGMENT = "/atendimento";
export const SALES_PIPELINE_SEGMENT = "/sales-pipeline";
export const AWARENESS_PIPELINE_SEGMENT = "/pipeline";
export const VIRTUAL_OFFICE_SEGMENT = "/escritorio-virtual";
export const WORKSPACE_NAV_SEARCH = "workspaceNav";

export function chatPath(leadId) {
  return leadId != null ? `/chat/${leadId}` : "/chat";
}

export function chatPathWithWorkspaceNav(leadId) {
  return withWorkspaceNav(chatPath(leadId));
}

export function isPopupCallPath(pathname = "") {
  const base = POPUP_CALL_BASE.replace(/\/+$/, "");
  const path = String(pathname || "").replace(/\/+$/, "") || "/";
  return path === base || path.startsWith(`${base}/`);
}

export function getServiceChatBasePath(basePath = TEAM_BASE) {
  const root = normalizeBase(basePath);
  if (isPopupTeamContext(root)) {
    return `${root}${SERVICE_SEGMENT}`;
  }
  return "/chat";
}

export function serviceChatPath(leadId, basePath = TEAM_BASE) {
  const root = getServiceChatBasePath(basePath).replace(/\/+$/, "");
  if (leadId == null) return root;
  return `${root}/${encodeURIComponent(String(leadId))}`;
}

export function isPopupServiceChatPath(pathname = "") {
  const base = `${POPUP_TEAM_BASE}${SERVICE_SEGMENT}`.replace(/\/+$/, "");
  const path = String(pathname || "").replace(/\/+$/, "") || "/";
  return path === base || path.startsWith(`${base}/`);
}

/** Navegação do atendimento conforme contexto (app com workspaceNav ou popup-equipe). */
export function resolveServiceChatNavPath(targetPath, { pathname = "", search = "" } = {}) {
  if (isPopupCallPath(pathname)) {
    const raw = String(targetPath || "");
    if (raw.startsWith(`${POPUP_CALL_BASE}/`) || raw === POPUP_CALL_BASE) return raw;
    const fromChat = /^\/chat\/(.+)$/.exec(raw);
    if (fromChat) return `${POPUP_CALL_BASE}/${encodeURIComponent(fromChat[1])}`;
    return raw || "/chat";
  }

  if (isPopupServiceChatPath(pathname)) {
    const popupBase = `${POPUP_TEAM_BASE}${SERVICE_SEGMENT}`.replace(/\/+$/, "");
    const raw = String(targetPath || "");
    if (raw === "/chat" || raw === "/" || raw === popupBase) return popupBase;
    const fromChat = /^\/chat\/(.+)$/.exec(raw);
    if (fromChat) return `${popupBase}/${encodeURIComponent(fromChat[1])}`;
    if (raw.startsWith(`${popupBase}/`) || raw === popupBase) return raw;
    return popupBase;
  }
  return preserveWorkspaceNavInPath(targetPath, search);
}

export function isServiceChatPath(pathname = "") {
  const path = String(pathname || "").replace(/\/+$/, "") || "/";
  if (isPopupCallPath(path)) return true;
  if (isPopupServiceChatPath(path)) return true;
  return path === "/" || path === "/chat" || path.startsWith("/chat/");
}

export function isWorkspaceNavSearch(search = "") {
  try {
    const params = new URLSearchParams(String(search || "").replace(/^\?/, ""));
    const value = params.get(WORKSPACE_NAV_SEARCH);
    return value === "1" || value === "true";
  } catch {
    return false;
  }
}

/** Marca a rota como aberta pela barra do workspace (mantém a barra no atendimento). */
export function withWorkspaceNav(path, { enabled = true } = {}) {
  if (!enabled) return path;
  const raw = String(path || "");
  const qIndex = raw.indexOf("?");
  const pathname = qIndex >= 0 ? raw.slice(0, qIndex) : raw;
  const params = new URLSearchParams(qIndex >= 0 ? raw.slice(qIndex + 1) : "");
  params.set(WORKSPACE_NAV_SEARCH, "1");
  const query = params.toString();
  return query ? `${pathname}?${query}` : `${pathname}?${WORKSPACE_NAV_SEARCH}=1`;
}

/** Preserva workspaceNav ao navegar dentro do atendimento. */
export function preserveWorkspaceNavInPath(path, sourceSearch = "") {
  if (!isWorkspaceNavSearch(sourceSearch)) return path;
  return withWorkspaceNav(path);
}

export function teamPath(spaceId, basePath = TEAM_BASE) {
  const root = normalizeBase(basePath);
  return spaceId != null ? `${root}/${spaceId}` : root;
}

export function getTasksBasePath(basePath = TEAM_BASE) {
  return `${normalizeBase(basePath)}${TASKS_SEGMENT}`;
}

export function teamTasksPaths(basePath = TEAM_BASE) {
  const tasksBase = getTasksBasePath(basePath);
  return {
    list: tasksBase,
    inbox: `${tasksBase}/inbox`,
    lead: (leadId) => `${tasksBase}/lead/${leadId}`,
    board: (boardId) => `${tasksBase}/${boardId}`,
    space: (spaceId) => `${tasksBase}/space/${spaceId}`,
  };
}

export function getAgendaBasePath(basePath = TEAM_BASE) {
  return `${normalizeBase(basePath)}${AGENDA_SEGMENT}`;
}

export function teamAgendaPaths(basePath = TEAM_BASE) {
  const agendaBase = getAgendaBasePath(basePath);
  return { root: agendaBase };
}

export function getSalesPipelineBasePath(basePath = TEAM_BASE) {
  return `${normalizeBase(basePath)}${SALES_PIPELINE_SEGMENT}`;
}

export function teamSalesPipelinePaths(basePath = TEAM_BASE) {
  const salesBase = getSalesPipelineBasePath(basePath);
  return {
    root: salesBase,
    lead: (leadId) => `${salesBase}/${encodeURIComponent(String(leadId))}`,
  };
}

export function getAwarenessPipelineBasePath(basePath = TEAM_BASE) {
  return `${normalizeBase(basePath)}${AWARENESS_PIPELINE_SEGMENT}`;
}

export function teamAwarenessPipelinePaths(basePath = TEAM_BASE) {
  const awarenessBase = getAwarenessPipelineBasePath(basePath);
  return { root: awarenessBase };
}

export function getVirtualOfficeBasePath(basePath = TEAM_BASE) {
  return `${normalizeBase(basePath)}${VIRTUAL_OFFICE_SEGMENT}`;
}

export function teamVirtualOfficePaths(basePath = TEAM_BASE) {
  const voBase = getVirtualOfficeBasePath(basePath);
  return { root: voBase };
}

export function isPopupTeamContext(basePath) {
  return normalizeBase(basePath).startsWith(POPUP_TEAM_BASE);
}

/** Rota atual é janela /popup-equipe (qualquer seção). */
export function isPopupEquipePath(pathname = "") {
  const path = String(pathname || "").replace(/\/+$/, "") || "/";
  return path === POPUP_TEAM_BASE || path.startsWith(`${POPUP_TEAM_BASE}/`);
}

/**
 * Base do módulo Equipe conforme a rota atual (cockpit popup vs app principal).
 * Use em navegações a partir de modais/sidebars compartilhados.
 */
export function resolveTeamBaseFromPathname(pathname = "") {
  return isPopupEquipePath(pathname) ? POPUP_TEAM_BASE : TEAM_BASE;
}

/** Seção ativa da barra inferior mobile conforme a rota atual. */
export function resolveWorkspaceActiveSection(pathname, basePath = TEAM_BASE) {
  const path = String(pathname || "").replace(/\/+$/, "") || "/";

  const popupServiceBase = `${POPUP_TEAM_BASE}${SERVICE_SEGMENT}`.replace(/\/+$/, "");
  if (isPopupCallPath(path)) {
    return "service";
  }
  if (path === popupServiceBase || path.startsWith(`${popupServiceBase}/`)) {
    return "service";
  }

  if (path === "/" || path === "/chat" || path.startsWith("/chat/")) {
    return "service";
  }

  const normBase = normalizeBase(basePath);
  const tasksBase = getTasksBasePath(normBase).replace(/\/+$/, "");
  const agendaBase = getAgendaBasePath(normBase).replace(/\/+$/, "");
  const salesPipelineBase = getSalesPipelineBasePath(normBase).replace(/\/+$/, "");
  const popupSalesPipelineBase = getSalesPipelineBasePath(POPUP_TEAM_BASE).replace(/\/+$/, "");
  const awarenessPipelineBase = getAwarenessPipelineBasePath(normBase).replace(/\/+$/, "");
  const popupAwarenessPipelineBase = getAwarenessPipelineBasePath(POPUP_TEAM_BASE).replace(/\/+$/, "");
  const voBase = getVirtualOfficeBasePath(normBase).replace(/\/+$/, "");
  const popupVoBase = getVirtualOfficeBasePath(POPUP_TEAM_BASE).replace(/\/+$/, "");

  if (path === voBase || path.startsWith(`${voBase}/`)) {
    return "virtualOffice";
  }

  if (path === popupVoBase || path.startsWith(`${popupVoBase}/`)) {
    return "virtualOffice";
  }

  if (path === agendaBase || path.startsWith(`${agendaBase}/`)) {
    return "agenda";
  }

  if (path === "/sales-pipeline" || path.startsWith("/sales-pipeline/")) {
    return "salesPipeline";
  }

  if (path === salesPipelineBase || path.startsWith(`${salesPipelineBase}/`)) {
    return "salesPipeline";
  }

  if (path === popupSalesPipelineBase || path.startsWith(`${popupSalesPipelineBase}/`)) {
    return "salesPipeline";
  }

  if (path === "/pipeline" || path.startsWith("/pipeline/")) {
    return "awareness";
  }

  if (path === awarenessPipelineBase || path.startsWith(`${awarenessPipelineBase}/`)) {
    return "awareness";
  }

  if (path === popupAwarenessPipelineBase || path.startsWith(`${popupAwarenessPipelineBase}/`)) {
    return "awareness";
  }

  if (path === `${tasksBase}/inbox` || path.startsWith(`${tasksBase}/inbox/`)) {
    return "inbox";
  }

  if (path === tasksBase || path.startsWith(`${tasksBase}/`)) {
    return "tasks";
  }

  if (path === normBase || path.startsWith(`${normBase}/`)) {
    return "chat";
  }

  if (path === "/platform/xprobase/team" || path.startsWith("/platform/xprobase/team/")) {
    return "chat";
  }

  if (path === "/partner/console/team" || path.startsWith("/partner/console/team/")) {
    return "chat";
  }

  return "service";
}

function normalizeBase(basePath) {
  return String(basePath || TEAM_BASE).replace(/\/+$/, "") || TEAM_BASE;
}

/** Viewport mobile do workspace (mesmo breakpoint do rail inferior). */
export function isMobileTeamViewport() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia("(max-width: 768px)").matches;
}

/** URL in-app (/equipe, /chat) equivalente ao popup de chat/atendimento. */
export function resolveTeamChatAppUrl(spaceId, { section } = {}) {
  const sid = spaceId != null ? String(spaceId).trim() : "";
  const useService = section === "atendimento" || section === "service";
  if (useService) {
    return sid ? chatPathWithWorkspaceNav(sid) : chatPathWithWorkspaceNav();
  }
  return teamPath(sid || undefined, TEAM_BASE);
}

/** URL in-app equivalente ao popup de tarefas. */
export function resolveTeamTasksAppUrl({ boardId, leadId, spaceId, inbox, scope, cardId } = {}) {
  const paths = teamTasksPaths(TEAM_BASE);
  let url;
  if (inbox) {
    url = paths.inbox;
    if (scope === "team" || scope === "created" || scope === "analytics") {
      url += `?scope=${encodeURIComponent(scope)}`;
    }
  } else if (leadId != null) {
    url = paths.lead(leadId);
  } else if (spaceId != null) {
    url = paths.space(spaceId);
  } else if (boardId != null) {
    url = paths.board(boardId);
  } else {
    url = paths.list;
  }

  if (cardId != null && Number(cardId) > 0 && !inbox && (boardId != null || leadId != null || spaceId != null)) {
    const sep = url.includes("?") ? "&" : "?";
    url += `${sep}card=${encodeURIComponent(String(cardId))}`;
  }
  return url;
}

/** URL in-app equivalente ao popup do pipeline. */
export function resolveSalesPipelineAppUrl({ leadId } = {}) {
  const paths = teamSalesPipelinePaths(TEAM_BASE);
  return leadId != null ? paths.lead(leadId) : paths.root;
}

/** URL in-app equivalente ao popup dos níveis de consciência. */
export function resolveAwarenessPipelineAppUrl() {
  return teamAwarenessPipelinePaths(TEAM_BASE).root;
}

/** Converte rota /popup-equipe/* para a rota equivalente no app principal. */
export function mapPopupEquipeToAppPath(pathname = "", search = "") {
  const path = String(pathname || "").replace(/\/+$/, "") || "/";
  if (!isPopupEquipePath(path)) return null;

  const popupRoot = POPUP_TEAM_BASE.replace(/\/+$/, "");
  if (path === popupRoot) return TEAM_BASE;

  const serviceBase = `${POPUP_TEAM_BASE}${SERVICE_SEGMENT}`.replace(/\/+$/, "");
  if (path === serviceBase || path.startsWith(`${serviceBase}/`)) {
    const leadPart = path.slice(serviceBase.length).replace(/^\//, "");
    return leadPart ? chatPathWithWorkspaceNav(leadPart) : chatPathWithWorkspaceNav();
  }

  const suffix = path.slice(popupRoot.length);
  const appPath = `${TEAM_BASE}${suffix}`;
  const qs = String(search || "").replace(/^\?/, "");
  return qs ? `${appPath}?${qs}` : appPath;
}
